import React, { useState, useRef, useEffect } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import clsx from "clsx";
import {
  BookOpenText,
  EyeOff,
  Headset,
  Flag,
  LogOut,
  Moon,
  Pencil,
  RotateCcw,
  Sun,
  SquareArrowOutUpRight,
  CircleQuestionMark,
} from "lucide-react";
import { useQueryClient } from "react-query";

import useUsers from "@/hooks/useUsers";
import { EditProfileModal } from "@/account/components/EditProfileModal/EditProfileModal";
import { Avatar } from "@/components/users/Avatar";
import { UserDisplay } from "@/components/users/UserDisplay";
import FPBreadcrumbs, {
  FPBreadcrumb,
} from "@/components/FPLayout/FPBreadcrumbs";
import FreshpaintLogoLight from "@/components/FpLogoLight";
import { Tooltip } from "@/design-system/components/Tooltip/Tooltip";
import { useTheme } from "@/theme/ThemeContext";
import { AccountFidgetSpinner } from "@/Topbar/AccountFidgetSpinner";
import { MasqueradeMenuItem } from "@/Topbar/MasqueradeMenuItem";
import { SimulatePermissionsMenuItem } from "@/Topbar/SimulatePermissionsMenuItem";
import { SimulatePermissionsModal } from "@/Topbar/SimulatePermissionsModal";
import { PreviewModeMenuSection } from "@/Topbar/PreviewModeMenuSection";
import useSupportFormURL from "@/Topbar/useSupportFormURL";
import { ROUTES } from "@/InsightsPage/constants";
import { startedFeaturesQueryKey } from "@/hooks/useHasEngagedWithFeature";
import { makeRequest, userIsFPEmployee } from "@/util";
import { getCurrentUser } from "@/auth/firebase";
import { isStudyPreview } from "@/config";
import type { ResetStartedFeaturesResponse } from "@/api/splashpages";

// pr-42.study.freshpaint-staging.com -> "42"
const studyPreviewPRNumber = isStudyPreview
  ? window.location.hostname.split(".")[0].replace(/^pr-/, "")
  : null;

interface TopBarProps {
  breadcrumbs?: FPBreadcrumb[];
  includeLogo?: boolean;
}

interface TopBarButtonProps {
  Icon: React.ComponentType<any>;
  label: string;
  url: string;
}

const TopBarButton = ({ Icon, label, url }: TopBarButtonProps) => {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="hover:bg-surface-hover focus:bg-surface-hover flex cursor-pointer items-center justify-between rounded-md p-2"
    >
      <div className="flex items-center gap-2">
        <span className="flex-shrink-0">
          <Icon />
        </span>
        <span className="text-textDefault block no-underline">{label}</span>
      </div>
      <span className="ml-2 flex-shrink-0">
        <SquareArrowOutUpRight size={16} className="text-iconMinimal" />
      </span>
    </a>
  );
};

export const TopBar = ({ breadcrumbs = [], includeLogo }: TopBarProps) => {
  const users = useUsers();
  const location = useLocation();
  const { theme, canToggle, toggleTheme } = useTheme();
  const queryClient = useQueryClient();

  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const helpRef = useRef<HTMLDivElement>(null);
  const [isUserProfileOpen, setIsUserProfileOpen] = useState(false);
  const [isEditProfileOpen, setIsEditProfileOpen] = useState(false);
  const [isSimulateOpen, setIsSimulateOpen] = useState(false);
  const userProfileRef = useRef<HTMLDivElement>(null);
  const [hasNotifications, setHasNotifications] = useState(false);

  const isInsightsPage = location.pathname === ROUTES.INSIGHTS_HOME;

  const supportFormURL = useSupportFormURL();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (helpRef.current && !helpRef.current.contains(event.target as Node)) {
        setIsHelpOpen(false);
      }
      if (
        userProfileRef.current &&
        !userProfileRef.current.contains(event.target as Node)
      ) {
        setIsUserProfileOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Listen for localStorage changes to show/hide notification
  useEffect(() => {
    const tourCompleted =
      localStorage.getItem("insightsTourCompleted") === "true";
    const hasEvents = localStorage.getItem("insightsHasEvents") === "true";
    setHasNotifications(tourCompleted && hasEvents);

    const handleStorageChange = () => {
      const tourCompleted =
        localStorage.getItem("insightsTourCompleted") === "true";
      const hasEvents = localStorage.getItem("insightsHasEvents") === "true";
      setHasNotifications(tourCompleted && hasEvents);
    };

    window.addEventListener("localStorageChanged", handleStorageChange);
    return () => {
      window.removeEventListener("localStorageChanged", handleStorageChange);
    };
  }, []);

  const toggleUserProfile = (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsUserProfileOpen(!isUserProfileOpen);
  };

  const toggleHelp = (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsHelpOpen(!isHelpOpen);
  };

  const handleNotificationClick = () => {
    localStorage.removeItem("insightsTourCompleted");
    setHasNotifications(false);
    // Dispatch custom event to notify other components
    window.dispatchEvent(new Event("localStorageChanged"));
  };

  return (
    <div
      className={clsx(
        "z-nav shadow-elevation-nav border-b-borderSubtle bg-surface-default relative flex w-full items-center border-b px-8 py-1",
        "",
      )}
    >
      {includeLogo && (
        <FreshpaintLogoLight height="36" className="[&_g]:!fill-blue-800" />
      )}
      <FPBreadcrumbs breadcrumbs={breadcrumbs} />
      {isStudyPreview && (
        <span className="text-textSubtle ml-4 text-sm font-medium">
          Preview build for PR #{studyPreviewPRNumber}
        </span>
      )}

      <div className="text-textSubtle ml-auto flex items-center gap-2">
        {hasNotifications && isInsightsPage && (
          <Tooltip content="Continue or revisit the Insights product tour here">
            <button
              onClick={handleNotificationClick}
              className="hover:bg-surface-selected focus:bg-surface-selected flex w-14 flex-col items-center rounded-md p-2 hover:cursor-pointer"
              data-track="data-insights-tour"
            >
              <Flag />
              <span className="text-iconMinimal text-xs font-semibold capitalize">
                Tour
              </span>
            </button>
          </Tooltip>
        )}
        <div ref={helpRef} className="relative">
          <button
            onClick={toggleHelp}
            className="hover:bg-surface-selected focus:bg-surface-selected flex w-14 flex-col items-center rounded-md p-2 hover:cursor-pointer"
            data-track="data-insights-help"
          >
            <CircleQuestionMark />
            <span className="text-iconMinimal text-xs font-semibold capitalize">
              Help
            </span>
          </button>
          {isHelpOpen && (
            <div className="shadow-elevation-200 border-interactive-selected bg-surface-default absolute right-0 z-100 mt-1 min-w-52 rounded-lg border p-2">
              <TopBarButton
                label="Documentation"
                url="https://documentation.freshpaint.io/your-freshpaint-account"
                Icon={BookOpenText}
              />
              <TopBarButton
                label="Create a ticket"
                url={supportFormURL.toString()}
                Icon={Headset}
              />
            </div>
          )}
        </div>
        <div ref={userProfileRef} className="relative">
          <button
            onClick={toggleUserProfile}
            aria-label="Account menu"
            className="hover:bg-surface-selected focus:bg-surface-selected flex h-12 w-12 items-center justify-center rounded-full p-2 hover:cursor-pointer"
          >
            <Avatar user={users.currentUser} fallbackLabel="FP" />
          </button>
          {isUserProfileOpen && (
            <div className="shadow-elevation-200 border-interactive-selected bg-surface-default absolute right-0 z-100 mt-1 min-w-64 rounded-lg border">
              <div className="group border-b-borderSubtle flex items-center gap-2 border-b px-4 py-2">
                <UserDisplay
                  user={users.currentUser}
                  fallbackLabel="FP"
                  variant="stacked"
                />
                {userIsFPEmployee() && <AccountFidgetSpinner />}
              </div>
              {userIsFPEmployee() && (
                <div
                  role="group"
                  aria-labelledby="account-menu-internal-only"
                  className="border-b-borderSubtle border-b p-2"
                >
                  <div
                    id="account-menu-internal-only"
                    className="text-label-xs text-textSubtle flex items-center gap-1 px-2 pt-1 pb-1 uppercase"
                  >
                    Internal only <EyeOff size={12} />
                  </div>
                  <MasqueradeMenuItem
                    onNavigate={() => setIsUserProfileOpen(false)}
                  />
                  <SimulatePermissionsMenuItem
                    onOpen={() => {
                      setIsSimulateOpen(true);
                      setIsUserProfileOpen(false);
                    }}
                  />
                  <button
                    onClick={async () => {
                      await makeRequest<ResetStartedFeaturesResponse>(
                        "reset_started_features",
                        {},
                      );
                      await queryClient.invalidateQueries(
                        startedFeaturesQueryKey(getCurrentUser()?.uid),
                      );
                    }}
                    className="hover:bg-surface-hover focus:bg-surface-hover flex w-full cursor-pointer items-center gap-2 rounded-md p-2 text-left"
                  >
                    <span className="flex-shrink-0">
                      <RotateCcw size={18} />
                    </span>
                    <span className="text-textDefault block">
                      Restore Splash Pages
                    </span>
                  </button>
                </div>
              )}
              {userIsFPEmployee() && <PreviewModeMenuSection />}
              <div className="border-b-borderSubtle border-b p-2">
                <button
                  onClick={() => {
                    setIsEditProfileOpen(true);
                    setIsUserProfileOpen(false);
                  }}
                  className="hover:bg-surface-hover focus:bg-surface-hover flex w-full cursor-pointer items-center gap-2 rounded-md p-2 text-left"
                >
                  <span className="flex-shrink-0">
                    <Pencil size={18} />
                  </span>
                  <span className="text-textDefault block">Edit profile</span>
                </button>
                {canToggle && (
                  <button
                    onClick={toggleTheme}
                    className="hover:bg-surface-hover focus:bg-surface-hover flex w-full cursor-pointer items-center gap-2 rounded-md p-2 text-left"
                  >
                    <span className="flex-shrink-0">
                      {theme === "dark" ? (
                        <Sun size={18} />
                      ) : (
                        <Moon size={18} />
                      )}
                    </span>
                    <span className="text-textDefault block">
                      {theme === "dark" ? "Light mode" : "Dark mode"}
                    </span>
                  </button>
                )}
              </div>
              <div className="flex items-center p-2">
                <RouterLink
                  to="/signout"
                  className="hover:bg-surface-hover focus:bg-surface-hover w-full rounded-md p-2"
                >
                  <div className="flex items-center gap-2">
                    <span className="flex-shrink-0">
                      <LogOut />
                    </span>
                    <span className="text-textDefault block no-underline">
                      Sign out
                    </span>
                  </div>
                </RouterLink>
              </div>
            </div>
          )}
        </div>
        <EditProfileModal
          open={isEditProfileOpen}
          onOpenChange={setIsEditProfileOpen}
        />
        <SimulatePermissionsModal
          open={isSimulateOpen}
          onOpenChange={setIsSimulateOpen}
        />
      </div>
    </div>
  );
};
